import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { FaStar } from 'react-icons/fa';
import { toast } from 'sonner';
import axios from 'axios';

export default function ReviewForm({ product, user, API, onReviewSubmitted }) {
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [comment, setComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (rating === 0) {
      toast.error('Veuillez sélectionner une note');
      return;
    }
    
    if (!comment.trim()) {
      toast.error('Veuillez ajouter un commentaire');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      await axios.post(`${API}/reviews`, {
        product_id: product.id,
        user_id: user.id,
        user_name: user.name,
        rating,
        comment
      });
      
      toast.success('Avis soumis avec succès ! Il sera visible après approbation par un administrateur.');
      
      // Reset form
      setRating(0);
      setComment('');
      
      if (onReviewSubmitted) {
        onReviewSubmitted();
      }
    } catch (error) {
      console.error('Error submitting review:', error);
      
      if (error.response?.status === 403) {
        toast.error('Vous n\'avez pas la permission de soumettre des avis. Contactez un administrateur.');
      } else if (error.response?.status === 400) {
        toast.error('Vous avez déjà soumis un avis pour ce produit.');
      } else {
        toast.error('Erreur lors de la soumission de l\'avis');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!user) {
    return (
      <Card>
        <CardContent className="p-6">
          <p className="text-gray-600">Connectez-vous pour soumettre un avis</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent className="p-6">
        <h3 className="text-xl font-bold text-[#0A2540] mb-4">Écrire un avis</h3>
        
        <form onSubmit={handleSubmit}>
          {/* Star Rating */}
          <div className="mb-4">
            <label className="block text-sm font-medium mb-2">Note</label>
            <div className="flex gap-2">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  onClick={() => setRating(star)}
                  onMouseEnter={() => setHoverRating(star)}
                  onMouseLeave={() => setHoverRating(0)}
                  className="focus:outline-none"
                >
                  <FaStar
                    className={`text-3xl transition-colors ${
                      star <= (hoverRating || rating)
                        ? 'text-yellow-400'
                        : 'text-gray-300'
                    }`}
                  />
                </button>
              ))}
            </div>
          </div>
          
          {/* Comment */}
          <div className="mb-4">
            <label className="block text-sm font-medium mb-2">Commentaire</label>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0A2540] focus:border-transparent"
              rows="4"
              placeholder="Partagez votre expérience avec ce produit..."
              required
            />
          </div>
          
          {/* Submit Button */}
          <Button
            type="submit"
            disabled={isSubmitting || rating === 0}
            className="w-full bg-[#635BFF] hover:bg-[#5248d9]"
          >
            {isSubmitting ? 'Envoi en cours...' : 'Soumettre l\'avis'}
          </Button>
          
          <p className="text-xs text-gray-500 mt-2">
            Votre avis sera visible après validation par un administrateur.
          </p>
        </form>
      </CardContent>
    </Card>
  );
}
